import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import * as Sentry from "@sentry/react";
import { api } from "../../convex/_generated/api";
import type { TagAlias } from "@/lib/tag-aliases";
import type { AiConsent } from "@/lib/ai-consent";
import { Id } from "../../convex/_generated/dataModel";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { SignInWithApple } from "@capacitor-community/apple-sign-in";
import { SocialLogin } from "@capgo/capacitor-social-login";

const APPLE_BUNDLE_ID = "com.eczy.app";

// Public OAuth client identifiers for native Google sign-in. The web client is
// reused as the ID token audience (verified server-side against AUTH_GOOGLE_ID);
// the iOS client is required by the GoogleSignIn SDK and must be set at build time.
const GOOGLE_WEB_CLIENT_ID = "1058595593656-c7q2rjvu0ct2ljqnhkj06kee1k2bje2f.apps.googleusercontent.com";
const GOOGLE_IOS_CLIENT_ID = import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID as string | undefined;

// SocialLogin must be initialized once before login; guard against re-init.
let socialLoginGoogleInit: Promise<void> | null = null;
const ensureGoogleSocialLogin = (): Promise<void> => {
  if (!socialLoginGoogleInit) {
    socialLoginGoogleInit = SocialLogin.initialize({
      google: {
        iOSClientId: GOOGLE_IOS_CLIENT_ID,
        iOSServerClientId: GOOGLE_WEB_CLIENT_ID,
        mode: "online",
      },
    }).catch((error) => {
      // Reset so a later attempt can retry initialization.
      socialLoginGoogleInit = null;
      throw error;
    });
  }
  return socialLoginGoogleInit;
};

// Random hex string bound to a single native Apple sign-in request. Apple echoes
// it back in the identity token, letting the server verify token freshness.
const generateNonce = (): string => {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

// The native sheet throws when the user backs out; don't surface that as an error.
const isUserCancellation = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return /cancel/i.test(message) || message.includes("1001") || /AuthorizationError/i.test(message);
};

type User = {
  id: string;
  email?: string;
  name?: string;
  image?: string;
  createdAt?: number;
  customPresetTags?: string[];
  tagAliases?: TagAlias[];
  imageDisplayDefault?: 'sticker' | 'normal';
  aiConsent?: AiConsent;
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  updateUserName: (name: string) => Promise<void>;
  updateCustomPresetTags: (tags: string[]) => Promise<void>;
  updateTagAliases: (aliases: { from: string; to: string }[]) => Promise<void>;
  updateImageDisplayDefault: (mode: 'sticker' | 'normal') => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const DEV_BYPASS = import.meta.env.DEV && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('bypass-auth');
const DEV_MOCK_USER: User = { id: 'dev-user', email: 'dev@example.com', name: 'Dev User' };
const LOGIN_PENDING_KEY = 'jilu:login-pending-at';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading: authLoading, isAuthenticated } = useConvexAuth();
  const { signIn, signOut: convexSignOut } = useAuthActions();
  const deleteAccountMutation = useMutation(api.auth.deleteAccount);
  const updateUserNameMutation = useMutation(api.auth.updateUserName);
  const updateCustomPresetTagsMutation = useMutation(api.auth.updateCustomPresetTags);
  const updateImageDisplayDefaultMutation = useMutation(api.auth.updateImageDisplayDefault);
  const updateTagAliasesMutation = useMutation(api.auth.updateTagAliases);
  const mobileRedirectTo = "com.eczy.app://auth";
  const isNativePlatform = Capacitor.isNativePlatform();
  const [isCompletingOAuth, setIsCompletingOAuth] = useState(() => {
    if (typeof window === "undefined" || isNativePlatform) return false;
    return new URLSearchParams(window.location.search).has("code");
  });

  const getOAuthRedirectTo = () => {
    if (isNativePlatform) return mobileRedirectTo;
    if (typeof window === "undefined") return undefined;
    return `${window.location.origin}/chat`;
  };

  useEffect(() => {
    if (!isNativePlatform) return;

    let removed = false;
    const listenerPromise = CapacitorApp.addListener("appUrlOpen", async ({ url }) => {
      if (!url || !url.startsWith(mobileRedirectTo)) return;

      const callbackUrl = new URL(url);
      const code = callbackUrl.searchParams.get("code");
      if (!code) return;

      try {
        await Browser.close();
      } catch {
        // Browser may already be closed.
      }

      try {
        await (signIn as any)(undefined, { code });
      } catch (error) {
        console.error(error);
        toast({
          title: "Login failed",
          description: error instanceof Error ? error.message : "An error occurred",
          variant: "destructive",
        });
      }
    });

    return () => {
      if (removed) return;
      removed = true;
      void listenerPromise.then((listener) => listener.remove());
    };
  }, [isNativePlatform, signIn]);

  useEffect(() => {
    if (isNativePlatform || typeof window === "undefined") return;

    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    if (!code) {
      setIsCompletingOAuth(false);
      return;
    }

    let cancelled = false;
    url.searchParams.delete("code");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);

    void (async () => {
      try {
        setIsCompletingOAuth(true);
        await (signIn as any)(undefined, { code });
      } catch (error) {
        if (cancelled) return;
        console.error(error);
        toast({
          title: "Login failed",
          description: error instanceof Error ? error.message : "Could not complete sign-in",
          variant: "destructive",
        });
      } finally {
        if (!cancelled) {
          setIsCompletingOAuth(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isNativePlatform, signIn]);
  
  // Wait for Convex auth bootstrap to finish before querying the user.
  const skipViewerQuery = DEV_BYPASS || isCompletingOAuth || authLoading || !isAuthenticated;
  const viewer = useQuery(api.auth.currentUser, skipViewerQuery ? "skip" : undefined);
  const loading =
    DEV_BYPASS ? false : isCompletingOAuth || authLoading || (isAuthenticated && viewer === undefined);

  const user: User | null = DEV_BYPASS ? DEV_MOCK_USER : viewer ? {
    id: viewer._id,
    email: viewer.email,
    name: viewer.name,
    image: viewer.image,
    createdAt: viewer.createdAt,
    customPresetTags: viewer.customPresetTags,
    tagAliases: viewer.tagAliases,
    imageDisplayDefault: viewer.imageDisplayDefault,
    aiConsent: viewer.aiConsent,
  } : null;

  useEffect(() => {
    if (user) {
      window.sessionStorage.removeItem(LOGIN_PENDING_KEY);
      Sentry.setUser({ id: user.id, email: user.email, username: user.name });
    } else {
      Sentry.setUser(null);
    }
  }, [user?.id, user?.email, user?.name]);

  const signInWithGoogle = async (): Promise<void> => {
    // Native iOS: use the GoogleSignIn system sheet (no in-app browser) and
    // exchange the resulting ID token with the server.
    if (isNativePlatform) {
      let idToken: string | null = null;
      try {
        if (!GOOGLE_IOS_CLIENT_ID) {
          throw new Error('Missing VITE_GOOGLE_IOS_CLIENT_ID for native Google sign-in');
        }
        await ensureGoogleSocialLogin();
        const { result } = await SocialLogin.login({
          provider: 'google',
          options: { scopes: ['email', 'profile'] },
        });
        idToken = 'idToken' in result ? result.idToken : null;
      } catch (error) {
        // A user-initiated cancel should quietly reset the UI, not toast.
        if (isUserCancellation(error)) {
          throw error;
        }
        console.error(error);
        toast({
          title: 'Login failed',
          description: error instanceof Error ? error.message : 'An error occurred',
          variant: 'destructive',
        });
        throw error;
      }

      try {
        if (!idToken) {
          throw new Error('Google did not return an ID token');
        }
        await signIn("google-native", { idToken });
      } catch (error) {
        console.error(error);
        toast({
          title: 'Login failed',
          description: error instanceof Error ? error.message : 'An error occurred',
          variant: 'destructive',
        });
        throw error;
      }
      return;
    }

    // Web: keep the existing Google OAuth redirect flow.
    try {
      const redirectTo = getOAuthRedirectTo();
      await signIn("google", redirectTo ? { redirectTo } : undefined);
    } catch (error) {
      console.error(error);
      toast({
        title: 'Login failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const signInWithApple = async (): Promise<void> => {
    // Native iOS: use the system "Sign in with Apple" sheet (Face ID / Touch ID,
    // no browser) and exchange the resulting identity token with the server.
    if (isNativePlatform) {
      const nonce = generateNonce();
      let response: Awaited<ReturnType<typeof SignInWithApple.authorize>>["response"];
      try {
        const result = await SignInWithApple.authorize({
          clientId: APPLE_BUNDLE_ID,
          redirectURI: mobileRedirectTo,
          scopes: "name email",
          nonce,
        });
        response = result.response;
      } catch (error) {
        // A user-initiated cancel should quietly reset the UI, not toast.
        if (isUserCancellation(error)) {
          throw error;
        }
        console.error(error);
        toast({
          title: 'Login failed',
          description: error instanceof Error ? error.message : 'An error occurred',
          variant: 'destructive',
        });
        throw error;
      }

      try {
        if (!response.identityToken) {
          throw new Error('Apple did not return an identity token');
        }
        await signIn("apple-native", {
          identityToken: response.identityToken,
          nonce,
          firstName: response.givenName ?? '',
          lastName: response.familyName ?? '',
        });
      } catch (error) {
        console.error(error);
        toast({
          title: 'Login failed',
          description: error instanceof Error ? error.message : 'An error occurred',
          variant: 'destructive',
        });
        throw error;
      }
      return;
    }

    // Web: keep the existing Apple OAuth redirect flow.
    try {
      const redirectTo = getOAuthRedirectTo();
      await signIn("apple", redirectTo ? { redirectTo } : undefined);
    } catch (error) {
      console.error(error);
      toast({
        title: 'Login failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleSignOut = async (): Promise<void> => {
    try {
      await convexSignOut();
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out',
      });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Logout failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteAccount = async (): Promise<void> => {
    if (!user) {
      throw new Error('No user logged in');
    }

    try {
      await deleteAccountMutation({ userId: user.id as Id<"users"> });
      await convexSignOut();
      
      toast({
        title: 'Account deleted',
        description: 'Your account and all data have been permanently deleted',
      });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateUserName = async (name: string): Promise<void> => {
    try {
      await updateUserNameMutation({ name });
      toast({
        title: 'Name updated',
        description: 'Your display name has been updated',
      });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Update failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateCustomPresetTags = async (tags: string[]): Promise<void> => {
    await updateCustomPresetTagsMutation({ tags });
  };

  const updateImageDisplayDefault = async (mode: 'sticker' | 'normal'): Promise<void> => {
    await updateImageDisplayDefaultMutation({ mode });
  };

  const updateTagAliases = async (
    aliases: { from: string; to: string }[]
  ): Promise<void> => {
    if (aliases.length === 0) return;
    await updateTagAliasesMutation({ aliases });
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    signInWithApple,
    signOut: handleSignOut,
    deleteAccount,
    updateUserName,
    updateCustomPresetTags,
    updateTagAliases,
    updateImageDisplayDefault,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
